import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import type { ComponentType, ReactNode } from 'react';

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE || 'development',
    release: import.meta.env.VITE_GIT_SHA,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
  });
}

type FallbackProps = {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError: () => void;
};

export function DefaultFallback({ eventId }: FallbackProps): ReactNode {
  const handleReload = () => {
    window.location.reload();
  };
  const handleReport = () => {
    Sentry.showReportDialog({ eventId });
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0A0B0F',
        color: '#C9CDDB',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: '24px',
      }}
    >
      <div
        style={{
          maxWidth: 480,
          width: '100%',
          backgroundColor: '#0B1437',
          border: '1px solid #13205A',
          borderRadius: 12,
          padding: '32px',
          textAlign: 'center',
          boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
        }}
      >
        <h1 style={{ color: '#D02736', fontSize: 24, margin: '0 0 12px' }}>Something broke</h1>
        <p style={{ color: '#9CA3AF', margin: '0 0 24px', lineHeight: 1.5 }}>
          We hit an unexpected error. Our team has been notified. You can reload the page or send
          us details about what you were doing.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleReload}
            style={{
              backgroundColor: '#D02736',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: 8,
              padding: '10px 20px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
          <button
            type="button"
            onClick={handleReport}
            style={{
              backgroundColor: 'transparent',
              color: '#C9CDDB',
              border: '1px solid #D02736',
              borderRadius: 8,
              padding: '10px 20px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Report Feedback
          </button>
        </div>
      </div>
    </div>
  );
}

export const SentryErrorBoundary: ComponentType<{
  children: ReactNode;
  fallback?: Sentry.FallbackRender;
}> = ({ children, fallback }) => {
  return (
    <Sentry.ErrorBoundary
      fallback={fallback ?? ((props) => <DefaultFallback {...props} />)}
      showDialog={false}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};
